import { SchoolExamResults, ChartData, ChartSection, ChartType, SchoolRating} from "../types/api.types";

const hasGrade = (grade: number | null | undefined) =>
  grade !== null && grade !== undefined && grade !== 0;

/**
 * Subjects that make up the school rating, in chart order.
 * 2025–2026 results split Lithuanian and mathematics into A and B courses
 * and renamed the English exam. A course is preferred, same as the rating.
 * Older imports keep the previous subject names.
 */
const RATING_SUBJECTS: { names: string[]; label: (name: string) => string }[] = [
  {
    names: ["Istorija", "Biologija"],
    label: (name) => (name === "Biologija" ? "Biologijos" : "Istorijos"),
  },
  {
    names: ["Užsienio (anglų) kalba", "Užsienio kalba (anglų)"],
    label: () => "Anglų kalbos",
  },
  {
    names: ["Matematika (A)", "Matematika (B)", "Matematika"],
    label: (name) =>
      name.endsWith("(A)")
        ? "Matematikos (A)"
        : name.endsWith("(B)")
          ? "Matematikos (B)"
          : "Matematikos",
  },
  {
    names: [
      "Lietuvių kalba ir literatūra (A)",
      "Lietuvių kalba ir literatūra (B)",
      "Lietuvių kalba ir literatūra",
    ],
    label: (name) =>
      name.endsWith("(A)")
        ? "Lietuvių kalbos (A)"
        : name.endsWith("(B)")
          ? "Lietuvių kalbos (B)"
          : "Lietuvių kalbos",
  },
];

export const processExamResults = (data: SchoolExamResults | null) => {
  const exams = data?.exams ?? [];

  return RATING_SUBJECTS.flatMap((slot) => {
    const match = slot.names
      .map((name) => exams.find((exam) => exam.subject_name === name))
      .find((exam) => exam && hasGrade(exam.average_grade));

    if (!match) {
      return [];
    }

    return [
      {
        ...match,
        subject_name: slot.label(match.subject_name).replace(" ", "\n"),
      },
    ];
  });
};

/** "2025-2026" is the school year whose exams were taken in 2026. */
export const formatExamYearLabel = (schoolYear?: string | null): string => {
  if (!schoolYear) {
    return "";
  }

  const examYear = schoolYear.split("-").pop()?.trim();
  return examYear ? `${examYear} metų` : "";
};

/** A and B courses were introduced with the 2026 exams (school year 2025-2026). */
export const examYearUsesCourseLevels = (schoolYear?: string | null): boolean => {
  const examYear = Number(schoolYear?.split("-").pop());
  return Number.isFinite(examYear) && examYear >= 2026;
};

export const createChartData = (
  examResults: SchoolExamResults | null,
  infraName: string | undefined
): ChartData => {
  const filteredExams = processExamResults(examResults);

  return {
    title: `Egzaminų rezultatai - ${infraName || ""}`,
    chart_type: ChartType.Bars,
    data: filteredExams.map((exam) => ({
      x: exam.subject_name,
      y: exam.average_grade,
    })),
    source:
      "https://www.nsa.smm.lt/egzaminai-ir-pasiekimu-patikrinimai/brandos-egzaminai/rezultatai/",
    section: ChartSection.SchoolExamResultsStatistics,
    layout: "horizontal",
  };
};

export const processSchoolRating = (data: SchoolRating | null): string => {
  if (!data) {
    return "-";
  }

  if (data.grade === 0 && data.error) {
    return "-";
  }

  const { city, city_count, municipality, municipality_count, country, country_count } =
    data.rating_position;

  const formatRating = (rating: number | null, count: number | null): string | null => {
    if (rating && count) {
      return rating > 10 ? `${rating}` : `${rating}/${Math.min(count, 10)}`;
    }
    return null;
  };

  return (
    formatRating(city, city_count) ||
    formatRating(municipality, municipality_count) ||
    formatRating(country, country_count) ||
    "-"
  );
};

export const getLocationDescription = (schoolRating: SchoolRating | null) => {
  if (!schoolRating) return "Lietuvos";
  return schoolRating.city || schoolRating.municipality || "Lietuvos";
};